import { Button, Stack, Text } from '@mantine/core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import AppModal from '@v2/commons/components/AppModal/AppModal.component'
import {
  type OrganizationSwitchTabSyncEvent,
  getLatestOrganizationSwitchEvent,
  getTabSyncCreatedAt,
  getTabSyncTabId,
  useTabSync,
} from '@v2/commons/hooks/useTabSync'
import { navigationState } from '@v2/commons/utils/navigation'
import { selectPendingOrganizationSwitchEvent } from '@v2/domains/organization/selectors/organizationSwitch.selectors'
import { receiveOrganizationSwitchEvent } from '@v2/domains/organization/store/organizationSwitch.store'
import {
  ORGANIZATION_SWITCH_EVENT_MAX_AGE,
  clearPendingOrganizationSwitchEvent,
  getLastExplicitLoginAt,
  getLastHandledOrganizationSwitchEventId,
  getPendingOrganizationSwitchEvent,
  markOrganizationSwitchEventHandled,
  setPendingOrganizationSwitchEvent,
} from '@v2/domains/organization/utils/organizationSwitchSession.utils'
import { isPublicRoute } from '@v2/domains/routing/utils/route-guards.utils'
import { selectCurrentOrganization } from '@v2/domains/user/selectors/user.selectors'
import { useAppDispatch, useAppSelector } from '@v2/store/hooks'

export const OrganizationSwitchSyncManager = () => {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const isPublic = isPublicRoute(pathname)
  const currentOrganization = useAppSelector(selectCurrentOrganization)
  const pendingEvent = useAppSelector(selectPendingOrganizationSwitchEvent)
  const lastExplicitLoginAt = useAppSelector(
    (state) => state.auth.lastExplicitLoginAt,
  )
  const tabIdRef = useRef(getTabSyncTabId())
  const tabCreatedAtRef = useRef(getTabSyncCreatedAt())
  const lastHandledEventIdRef = useRef(
    getLastHandledOrganizationSwitchEventId(),
  )
  const latestQueuedTimestampRef = useRef(0)
  const [shownNoticeTimestamp, setShownNoticeTimestamp] = useState(0)

  const queueEvent = useCallback(
    (event: OrganizationSwitchTabSyncEvent | null) => {
      const lastLoginAt = Math.max(
        lastExplicitLoginAt,
        getLastExplicitLoginAt(),
      )
      if (
        !event ||
        !currentOrganization.id ||
        event.sourceTabId === tabIdRef.current ||
        event.eventId === lastHandledEventIdRef.current ||
        event.timestamp <= tabCreatedAtRef.current ||
        event.timestamp <= lastLoginAt ||
        event.timestamp < latestQueuedTimestampRef.current ||
        Date.now() - event.timestamp > ORGANIZATION_SWITCH_EVENT_MAX_AGE
      ) {
        return
      }

      latestQueuedTimestampRef.current = event.timestamp
      dispatch(receiveOrganizationSwitchEvent(event))
      setPendingOrganizationSwitchEvent(event)
      // Once shown, keep the overlay open in the background until a new login.
      setShownNoticeTimestamp((previousTimestamp) =>
        !isPublic && (!document.hidden || previousTimestamp > lastLoginAt)
          ? event.timestamp
          : 0,
      )
    },
    [currentOrganization.id, dispatch, lastExplicitLoginAt, isPublic],
  )

  const activePendingEvent =
    pendingEvent && pendingEvent.timestamp > lastExplicitLoginAt
      ? pendingEvent
      : null

  useTabSync({
    onOrgSwitch: queueEvent,
  })

  const recoverPendingEvents = useCallback(() => {
    // Consider every source: an older persisted notice must not hide a newer
    // shared event, and in-memory state must survive unavailable storage.
    queueEvent(pendingEvent)
    queueEvent(getPendingOrganizationSwitchEvent())
    queueEvent(getLatestOrganizationSwitchEvent())
  }, [pendingEvent, queueEvent])

  useEffect(recoverPendingEvents, [recoverPendingEvents])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        recoverPendingEvents()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [recoverPendingEvents])

  const handleContinue = () => {
    if (!activePendingEvent || isPublic) {
      return
    }

    lastHandledEventIdRef.current = activePendingEvent.eventId
    markOrganizationSwitchEventHandled(activePendingEvent.eventId)
    clearPendingOrganizationSwitchEvent()
    // Keep Redux blocked until the new document restores the shared session.
    navigationState.replace(activePendingEvent.targetPath)
  }

  if (!activePendingEvent || isPublic) {
    return null
  }

  return (
    <AppModal
      opened={shownNoticeTimestamp > lastExplicitLoginAt}
      onClose={() => {}}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size="md"
      title="Organization changed"
      overlayProps={{ backgroundOpacity: 0.85, blur: 9 }}
      attributes={{
        overlay: { 'data-testid': 'organization-switch-background-modal' },
      }}
    >
      <Stack gap="lg" data-testid="organization-switch-changed-modal">
        <Text>
          Your organization was changed in another tab. You are now working in{' '}
          <Text
            component="span"
            fw={700}
            data-testid="organization-switch-current-organization-label"
          >
            {activePendingEvent.organizationName}
          </Text>
          .
        </Text>

        <Text c="dimmed" size="sm">
          Continue to reload this tab in the correct organization context.
        </Text>

        <Button
          data-testid="organization-switch-continue-button"
          onClick={handleContinue}
          fullWidth
        >
          Continue
        </Button>
      </Stack>
    </AppModal>
  )
}
